import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import Button, { IconButton } from '@components/button/Button';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { Icon, Table } from '@components/index';
import NumberStepper from '@components/number-stepper/NumberStepper';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT
} from '@constants/generic';
import {
  customTermMemberTableColumn,
  customTermTableColumn
} from '@features/group-management/constants';
import { getFeeCategoryDataFromConfig } from '@features/group-management/helper';
import {
  getUpdatedTermsList,
  isDateBefore,
  onAddTerm,
  onTermDelete
} from '@features/group-management/helpers/customTerm';
import { CustomTermTableProps, FeeConfigurationType, TermsList } from '@types';
import { formatISODate } from '@utils/date';

import TermDueAmountField from './TermDueAmountField';
import TermDueDateSelection from './TermDueDateSelection';
import TermStartDate from './TermStartDate';

import theme from '../../../../../../themes/colors';

const CustomTermTable = ({
  termsList,
  setTermsList,
  isFeeCategoryEnabled,
  feeCategories,
  onAmountChange,
  isMember = false,
  isEditGroup = false,
  error,
  setError,
  groupEndDate,
  memberFlow,
  endDateError,
  setEndDateError
}: CustomTermTableProps) => {
  const disabledRowIds: string[] = [];
  const { orgConfig } = useSelector((state: any) => state.user);

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;

  const handleAddTerm = () => {
    const { term, endDateError: newEndDateError } = onAddTerm(
      termsList,
      feeCategories,
      groupEndDate
    );
    setTermsList(term);
    setEndDateError?.(newEndDateError);
  };

  const handleTermDelete = (termId: string) => {
    const {
      termsList: updatedTermsList,
      endDateError: newEndDateError,
      totalAmount
    } = onTermDelete(termId, termsList, groupEndDate);

    setTermsList(updatedTermsList);
    setEndDateError?.(newEndDateError);
    onAmountChange(totalAmount.toString());
  };

  const handleTermFieldEdit = (termId: string, field: string, value: any) => {
    let updatedTermsList;

    if (field === 'termDuration' || field === 'termStartDate') {
      const { updatedTermsListData, dateError } = getUpdatedTermsList(
        termsList,
        termId,
        field,
        value,
        groupEndDate
      );
      updatedTermsList = updatedTermsListData;
      setEndDateError?.(dateError);
    } else {
      updatedTermsList = termsList.map((termDetails) =>
        termDetails.id === termId
          ? { ...termDetails, [field]: value }
          : termDetails
      );
    }
    setTermsList(updatedTermsList);

    if (field === 'dueAmount') {
      let totalAmount = 0;
      updatedTermsList.forEach((termDetails) => {
        if (termDetails.id === termId) {
          totalAmount += Number(value);
        } else totalAmount += Number(termDetails.dueAmount);
      });

      onAmountChange(totalAmount.toString());
    }
  };

  const onFeeCategoryAmountSave = (
    termFeeConfig: FeeConfigurationType[],
    termIndex: number
  ) => {
    const term = termsList[termIndex];
    const termFeeCategoryData = getFeeCategoryDataFromConfig(termFeeConfig);
    const newTotalAmount = termFeeCategoryData.reduce(
      (sum, category) => sum + (Number(category.value) || 0),
      0
    );

    const updatedTermsList = termsList.map((termDetails) =>
      termDetails.id === term.id
        ? {
            ...termDetails,
            feeCategoryData: termFeeConfig,
            dueAmount: newTotalAmount.toString()
          }
        : termDetails
    );

    const totalAmount = updatedTermsList.reduce(
      (sum, termDetails) => sum + Number(termDetails.dueAmount),
      0
    );

    onAmountChange(totalAmount.toString());

    setTermsList(updatedTermsList);
  };

  const getTermEndDatePassed = (row: TermsList) =>
    isDateBefore(row.termEndDate, new Date());

  const modifiedTableData = termsList?.map((row: TermsList, index) => {
    const isDisabled =
      // eslint-disable-next-line no-nested-ternary
      !isMember && !isEditGroup
        ? false
        : memberFlow === 'edit' || isEditGroup
        ? getTermEndDatePassed(row) || row.isPaymentLinkSent === true
        : getTermEndDatePassed(row);

    if (isDisabled) disabledRowIds.push(row.id);

    const isDueDateError =
      !!error?.dueDate &&
      isDateBefore(row.dueDate, new Date()) &&
      !isDisabled &&
      row.dueAmount !== '0';

    const dueDate =
      !isEditGroup && !isMember && isDateBefore(row.dueDate, new Date())
        ? ''
        : row.dueDate;

    return {
      id: row.id,
      term: <div className="max-w-125 truncate">{row.term}</div>,
      termDurationMonths:
        isMember || isDisabled || isEditGroup ? (
          row.termDuration
        ) : (
          <NumberStepper
            value={row.termDuration}
            onChange={(value) =>
              handleTermFieldEdit(row.id, 'termDuration', value)
            }
            minValue={1}
            maxValue={12}
            className="max-w-50"
          />
        ),
      startDate:
        index === 0 && !isMember && !isEditGroup ? (
          <TermStartDate
            startDate={row.termStartDate}
            onStartDateChange={(value) =>
              handleTermFieldEdit(row.id, 'termStartDate', value)
            }
          />
        ) : (
          <div className="flex justify-start">
            <p className={`text-base ${isDisabled ? '' : 'text-content'}`}>
              {formatISODate({
                date: row.termStartDate,
                format: 'DD MMM YYYY'
              })}
            </p>
          </div>
        ),
      endDate: (
        <div className="flex justify-start">
          <p className={`text-base ${isDisabled ? '' : 'text-content'}`}>
            {formatISODate({
              date: row.termEndDate,
              format: 'DD MMM YYYY'
            })}
          </p>
        </div>
      ),
      dueDate: (
        <TermDueDateSelection
          dueDate={dueDate}
          termStartDate={row.termStartDate}
          termEndDate={row.termEndDate}
          onDueDateChange={(value) => {
            handleTermFieldEdit(row.id, 'dueDate', value);
            setError?.({ ...error, dueDate: '' });
          }}
          isDueDateDisabled={isDisabled}
          isDueDateError={isDueDateError}
          dueDateErrorMessage={error?.dueDate}
        />
      ),
      amount: (
        <TermDueAmountField
          row={row}
          rowIndex={index}
          isFeeCategoryEnabled={isFeeCategoryEnabled}
          feeCategoryData={row.feeCategoryData || []}
          onFeeCategorySave={(params: FeeConfigurationType[]) =>
            onFeeCategoryAmountSave(params, index)
          }
          onAmountChange={(value) =>
            handleTermFieldEdit(row.id, 'dueAmount', value)
          }
          isDisabled={isDisabled}
        />
      ),
      ...(!isMember &&
        !isEditGroup && {
          actions: (
            <IconButton
              iconName={index === 0 ? 'trash' : 'trash-red-2'}
              variant="transparent"
              size="none"
              className={`m-0 ${
                index === 0 ? 'cursor-not-allowed opacity-40' : ''
              }`}
              handleClick={() => handleTermDelete(row.id)}
              disabled={index === 0}
            />
          )
        })
    };
  });

  useEffect(() => {
    let hasError = false;

    if (termsList.length > 0 && groupEndDate) {
      hasError = isDateBefore(
        groupEndDate,
        new Date(termsList[termsList.length - 1].termEndDate)
      );
    }

    if (groupEndDate === '' || !hasError) {
      setEndDateError?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupEndDate]);

  useEffect(() => {
    if (termsList.length > 0 && groupEndDate) {
      setEndDateError?.(
        isDateBefore(
          groupEndDate,
          new Date(termsList[termsList.length - 1].termEndDate)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsList.length]);

  const amountWithIcon = (amount: string, compact = false) => (
    <CurrencyAmount
      value={Number(amount)}
      className={compact ? 'inline-flex items-center' : 'flex items-center'}
      symbolClass="font-normal !text-sm"
      valueClass={
        compact ? 'font-medium !text-sm' : 'pl-1 font-medium !text-sm'
      }
    />
  );

  return (
    <>
      <div className="flex h-fit flex-col justify-start rounded-lg border border-grey30 p-4">
        <Table
          columns={
            isMember || isEditGroup
              ? customTermMemberTableColumn
              : customTermTableColumn
          }
          data={modifiedTableData}
          disabledRowIds={disabledRowIds}
          disabledCellStyle="!text-[rgba(140,140,140,0.5)] !bg-black !bg-opacity-[0.01]"
          onHoverText="The term is complete"
        />
        {endDateError && (
          <div className="flex items-center gap-2 text-sm text-error">
            <Icon name="alert-circle" size="none" stroke={theme.error} />
            Terms cannot extend beyond the selected Group Deactivation Date
          </div>
        )}
        {!isMember && !isEditGroup && (
          <Button
            variant="secondary"
            label="Add Term"
            handleClick={handleAddTerm}
            leftIconName="plus"
            leftIconStroke={theme.primary}
            className="mt-3 w-36"
          />
        )}
      </div>
      <div className="flex flex-col items-start gap-1 pb-3 pt-1 text-base text-grey80">
        <div className="text-base text-grey80">Notes :</div>
        <div className="ml-4 flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2">
            <span>- You can enter any amount between</span>
            {amountWithIcon(groupMemberMinAmount, true)}
            <span>and</span>
            {amountWithIcon(groupMemberMaxAmount, true)}
          </div>
          <div className="flex flex-wrap items-center gap-x-2">
            <span>- If a term doesn&apos;t apply, simply enter</span>
            <span className="inline-flex items-center gap-x-0">
              &quot;{amountWithIcon('0', true)}&quot;
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomTermTable;
